import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { ToastModule } from 'primeng/toast';
import { ClusterService } from '../../../../core/services/cluster-service';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { Cluster } from '../../../../core/models/cluster';
import { RegexConstants } from '../../../../core/constants/regex-constants';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { RequiredMarkDirective } from '../../../../shared/directives/required-mark.directive';

@Component({
  selector: 'em-cluster-form',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, InputTextModule, ButtonModule, ToastModule, TranslatePipe, RequiredMarkDirective],
  templateUrl: './cluster-form.component.html',
  styleUrl: './cluster-form.component.scss',
  providers: [MessageService],
})
export class ClusterFormComponent {
  form!: FormGroup;
  formErrors: any;
  isEditMode: boolean = false;
  errorMessages: any = {};
  translations: any = {};
  originalCluster!: Cluster;
  edited: boolean = false;
  constructor(
    private fb: FormBuilder,
    private clusterService: ClusterService,
    private ref: DynamicDialogRef,
    private messageService: MessageService,
    private config: DynamicDialogConfig,
    private translate: TranslateService,
  ) {}

  ngOnInit() {
    this.loadTranslations();
    this.loadErrorMessages();

    this.form = this.fb.group({
      id: [null],
      name: [null, Validators.compose([
        Validators.required,
         Validators.pattern(RegexConstants.CLUSTER_NAME)])],
      nameEs: [null,
         Validators.compose([
          Validators.required,
           Validators.pattern(RegexConstants.CLUSTER_NAME_ES)])],
      nameAr: [null,
         Validators.compose([
          Validators.required,
           Validators.pattern(RegexConstants.CLUSTER_NAME_AR)])],
    });

    if (this.config.data) {
      this.isEditMode = true;
      this.originalCluster = JSON.parse(JSON.stringify(this.config.data));
      this.populateForm(this.config.data);
    }
    this.translate.onLangChange.subscribe(() => {
      this.loadErrorMessages();
    });
    this.form.valueChanges.subscribe(() => {
      this.edited = true;
    });
  }

  loadErrorMessages(): void {
    this.translate
      .get([
        'layout.configurations.cluster.ClusterValidators.required.nameEn',
        'layout.configurations.cluster.ClusterValidators.required.nameEs',
        'layout.configurations.cluster.ClusterValidators.required.nameAr',
        'layout.configurations.cluster.ClusterValidators.pattern.nameEn',
        'layout.configurations.cluster.ClusterValidators.pattern.nameEs',
        'layout.configurations.cluster.ClusterValidators.pattern.nameAr',
      ])
      .subscribe((translations) => {
        this.errorMessages = {
          required: {
            name: translations['layout.configurations.cluster.ClusterValidators.required.nameEn'],
            nameEs: translations['layout.configurations.cluster.ClusterValidators.required.nameEs'],
            nameAr: translations['layout.configurations.cluster.ClusterValidators.required.nameAr'],
          },
          pattern: {
            name: translations['layout.configurations.cluster.ClusterValidators.pattern.nameEn'],
            nameEs: translations['layout.configurations.cluster.ClusterValidators.pattern.nameEs'],
            nameAr: translations['layout.configurations.cluster.ClusterValidators.pattern.nameAr'],
          },
        };
      });
  }

  loadTranslations() {
    this.translate
      .get([
        'layout.configurations.cluster.editCluster.errorMessage.title',
        'layout.configurations.cluster.editCluster.errorMessage.alreadyExists',
        'layout.configurations.cluster.addCluster.errorMessage.title',
        'layout.configurations.cluster.addCluster.errorMessage.alreadyExists',
      ])
      .subscribe((translations) => {
        this.translations = translations;
      });
  }

  populateForm(cluster: Cluster) {
    this.form.patchValue({
      id: cluster.id,
      name: cluster.name,
      nameEs: cluster.nameEs,
      nameAr: cluster.nameAr,
    });
  }

  onSave() {
    if (this.form.invalid) {
      Object.values(this.form.controls).forEach((control) => {
        control.markAsTouched();
      });
      return;
    }

    if (this.isEditMode) {
      this.updateCluster();
    } else {
      this.addCluster();
    }
  }

  addCluster() {
    const payload = { ...this.form.value };
    delete payload.id;  

    this.clusterService.addCluster(payload).subscribe({
      next: (cluster: Cluster) => {
         this.ref.close(cluster);
    },
      error: (error) => {
        this.formErrors = error?.error?.fieldErrors;
        console.log('Error while saving the cluster: ', error);
        this.messageService.add({
          severity: 'error',
          summary: this.translations['layout.configurations.cluster.addCluster.errorMessage.title'],
          detail: this.translations['layout.configurations.cluster.addCluster.errorMessage.alreadyExists'],
        });
      },
    });
  }

  updateCluster() {
    const updated = this.form.value;
    const isChanged = JSON.stringify(this.originalCluster) !== JSON.stringify(updated);
     if (!isChanged) {
      this.ref.close({ type: 'no-change' });
      return;
    }
    this.clusterService.updateCluster(updated).subscribe({
      next: (cluster: Cluster) => {
        this.ref.close({ type: 'updated', data: cluster });
      },
      error: (error) => {
        this.formErrors = error?.error?.fieldErrors;
        console.log('Error while updating the cluster: ', error);
        this.messageService.add({
          severity: 'error',
          summary: this.translations['layout.configurations.cluster.editCluster.errorMessage.title'],
          detail: this.translations['layout.configurations.cluster.editCluster.errorMessage.alreadyExists'],
        });
      },
    });
  }

  isInvalid(key: string) {
    const control = this.form.get(key);

    return {
      invalid: control?.invalid && (control?.dirty || control?.touched),
      message:
        control?.errors &&
        (control.errors['required'] ? this.errorMessages.required[key] 
          : control.errors['minlength']
          ? this.errorMessages.minLength[key]
          : this.errorMessages.pattern[key]),
    };
  }

  getErrorMessage(field: string) {
    return this.formErrors?.find((obj: { field: string }) => obj.field == field)?.errorMessage || '';
  }
}
